import request from '@/utils/request'

/**
 * 获取用户桥梁关联列表
 * @param {Object} [params] 查询参数
 * @param {number} [params.userId] 用户ID
 * @param {number} [params.bridgeId] 桥梁ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     userId: number,
 *     username: string,
 *     realName: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     bridgeCode: string,
 *     roleType: number,
 *     permissions: Array<string>,
 *     createTime: string,
 *     updateTime: string,
 *     isDeleted: boolean
 *   }>
 * }>} 用户桥梁关联列表
 */
export function getUserBridgeAssociations(params) {
  return request({
    url: '/user-bridge',
    method: 'get',
    params
  })
}

/**
 * 创建用户桥梁关联
 * @param {Object} data 关联数据
 * @param {number} data.userId 用户ID
 * @param {number} [data.bridgeId] 桥梁ID
 * @param {Array<number>} [data.bridgeIds] 多个桥梁ID列表，用于批量关联
 * @param {number} [data.roleType] 角色类型：1-桥梁管理员，2-值班室用户
 * @param {Array<string>} [data.permissions] 权限标识列表
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     userId: number,
 *     username: string,
 *     realName: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     bridgeCode: string,
 *     roleType: number,
 *     permissions: Array<string>,
 *     createTime: string,
 *     updateTime: string,
 *     isDeleted: boolean
 *   }
 * }>} 创建的用户桥梁关联
 */
export function createUserBridgeAssociation(data) {
  return request({
    url: '/user-bridge',
    method: 'post',
    data
  })
}

/**
 * 更新用户桥梁关联
 * @param {number} id 关联ID
 * @param {Object} data 关联数据
 * @param {number} data.userId 用户ID
 * @param {number} [data.bridgeId] 桥梁ID
 * @param {number} [data.roleType] 角色类型：1-桥梁管理员，2-值班室用户
 * @param {Array<string>} [data.permissions] 权限标识列表
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     userId: number,
 *     username: string,
 *     realName: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     bridgeCode: string,
 *     roleType: number,
 *     permissions: Array<string>,
 *     createTime: string,
 *     updateTime: string,
 *     isDeleted: boolean
 *   }
 * }>} 更新后的用户桥梁关联
 */
export function updateUserBridgeAssociation(id, data) {
  return request({
    url: `/user-bridge/${id}`,
    method: 'put',
    data
  })
}

/**
 * 删除用户桥梁关联
 * @param {number} id 关联ID
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 删除结果
 */
export function deleteUserBridgeAssociation(id) {
  return request({
    url: `/user-bridge/${id}`,
    method: 'delete'
  })
}

/**
 * 根据ID获取用户桥梁关联详情
 * @param {number} id 关联ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     userId: number,
 *     username: string,
 *     realName: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     bridgeCode: string,
 *     roleType: number,
 *     permissions: Array<string>,
 *     createTime: string,
 *     updateTime: string,
 *     isDeleted: boolean
 *   }
 * }>} 用户桥梁关联详情
 */
export function getUserBridgeAssociationById(id) {
  return request({
    url: `/user-bridge/${id}`,
    method: 'get'
  })
}

/**
 * 根据用户ID获取关联的桥梁
 * @param {number} userId 用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     userId: number,
 *     username: string,
 *     realName: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     bridgeCode: string,
 *     roleType: number,
 *     permissions: Array<string>,
 *     createTime: string,
 *     updateTime: string,
 *     isDeleted: boolean
 *   }>
 * }>} 用户关联的桥梁列表
 */
export function getBridgesByUserId(userId) {
  return request({
    url: `/user-bridge/user/${userId}`,
    method: 'get'
  })
}

/**
 * 根据桥梁ID获取关联的用户
 * @param {number} bridgeId 桥梁ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     userId: number,
 *     username: string,
 *     realName: string,
 *     bridgeId: number,
 *     bridgeName: string,
 *     bridgeCode: string,
 *     roleType: number,
 *     permissions: Array<string>,
 *     createTime: string,
 *     updateTime: string,
 *     isDeleted: boolean
 *   }>
 * }>} 桥梁关联的用户列表
 */
export function getUsersByBridgeId(bridgeId) {
  return request({
    url: `/user-bridge/bridge/${bridgeId}`,
    method: 'get'
  })
}

/**
 * 获取用户可访问的桥梁ID列表
 * @param {number} userId 用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<number>
 * }>} 用户可访问的桥梁ID列表
 */
export function getUserAccessibleBridges(userId) {
  return request({
    url: `/user-bridge/accessible-bridges/${userId}`,
    method: 'get'
  })
}

/**
 * 检查用户是否有权限访问指定桥梁
 * @param {number} userId 用户ID
 * @param {number} bridgeId 桥梁ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: boolean
 * }>} 检查结果
 */
export function checkUserBridgeAccess(userId, bridgeId) {
  return request({
    url: '/user-bridge/check-access',
    method: 'get',
    params: { userId, bridgeId }
  })
}

/**
 * 移除用户对桥梁的访问权限
 * @param {number} userId 用户ID
 * @param {number} bridgeId 桥梁ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: boolean
 * }>} 移除结果
 */
export function removeUserBridgeAccess(userId, bridgeId) {
  return request({
    url: '/user-bridge/remove-access',
    method: 'delete',
    params: { userId, bridgeId }
  })
}

/**
 * 批量为一个用户分配多个桥梁
 * @param {Object} data 批量分配数据
 * @param {number} data.userId 用户ID
 * @param {Array<number>} data.bridgeIds 桥梁ID列表
 * @param {number} [data.roleType] 角色类型：1-桥梁管理员，2-值班室用户
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 分配结果
 */
export function batchAssignBridges(data) {
  return request({
    url: '/user-bridge/batch-assign-bridges',
    method: 'post',
    data
  })
}

/**
 * 批量为一个桥梁分配多个用户
 * @param {Object} data 批量分配数据
 * @param {number} data.bridgeId 桥梁ID
 * @param {Array<number>} data.userIds 用户ID列表
 * @param {number} [data.roleType] 角色类型：1-桥梁管理员，2-值班室用户
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 分配结果
 */
export function batchAssignUsers(data) {
  return request({
    url: '/user-bridge/batch-assign-users',
    method: 'post',
    data
  })
} 